import React from 'react'

import { InputUI, useModalContext, withFormikSupport } from '@fourthwall/components'
import { ArrowShortDownIcon } from '@fourthwall/icons'
import { getPrice } from '@fourthwall/utils'

import { useThirdPartyLogisticsOptionsQuery } from '../../../../../../hooks/useThirdPartyLogisticsOptionsQuery'
import type { OfferQueryResponse } from '../../../../../hooks'
import * as S from './PackageSizeInput.styled'
import type { ProductSize } from './PackageSizeModal'
import { PackageSizeModal } from './PackageSizeModal'
import { EXAMPLES_BY_SIZES, LABELS } from './labels'
import type { Size } from './types'

type PackageSizeSelectProps = {
  error?: string
  value?: Size
  onChange(value: string): void
  shippingConfig: OfferQueryResponse['config']['shippingConfig']
}

const getPackageSizes = (
  sizes: {
    size: Size
    maxDimensions: string
    maxVolume: string
    storageFee: {
      value: number
      currency: string
    }
  }[],
): ProductSize[] => {
  return sizes.map((size) => {
    return {
      size: size.size,
      examples: EXAMPLES_BY_SIZES[size.size],
      maxSize: {
        dimensions: size.maxDimensions,
        volume: `Up to ${size.maxVolume} cubic inches`,
      },
      storage: {
        feePerMonth: size.storageFee.value,
      },
      // 'Small ($0.05/month per item for storage)'
      inputLabel: `${LABELS[size.size]} (${getPrice(size.storageFee.value, { currency: size.storageFee.currency, minimumFractionDigits: 2 })}/month per item for storage)`,
    }
  })
}

const PackageSizeInput = React.memo(
  ({ error, value, shippingConfig, onChange }: PackageSizeSelectProps) => {
    const { open, close } = useModalContext()
    const { thirdPartyLogisticsOptionsQuery } = useThirdPartyLogisticsOptionsQuery()

    if (!thirdPartyLogisticsOptionsQuery.data) {
      return null
    }

    const { sizes } = thirdPartyLogisticsOptionsQuery.data

    const openModalWithOptions = async () => {
      if (!shippingConfig?.shippingPackageSizeEditable) {
        return
      }

      open(PackageSizeModal, {
        currentValue: value,
        sizesData: getPackageSizes(sizes),
        onClose: () => {
          close()
        },
        onConfirm: async (value: string) => {
          onChange(value)
        },
      })
    }

    const getInputValue = () => {
      if (
        shippingConfig.shippingPackageSize === 'CUSTOM' &&
        shippingConfig.shippingPackageStorageFee
      ) {
        return `Custom (${getPrice(shippingConfig.shippingPackageStorageFee.value, {
          currency: shippingConfig.shippingPackageStorageFee.currency,
          minimumFractionDigits: 2,
        })}/month per item for storage)`
      }

      return value ? (getPackageSizes(sizes).find((s) => s.size === value)?.inputLabel ?? '') : ''
    }

    return (
      <S.PackageSizeInput>
        <InputUI.Root $fullWidth>
          <InputUI.Box
            $invalid={Boolean(error)}
            $disabled={!shippingConfig?.shippingPackageSizeEditable}
          >
            <InputUI.Container
              onClick={openModalWithOptions}
              style={{
                cursor: !shippingConfig?.shippingPackageSizeEditable ? 'not-allowed' : 'pointer',
              }}
            >
              <InputUI.IconContainer style={{ right: 15, top: 18 }}>
                <ArrowShortDownIcon />
              </InputUI.IconContainer>

              <InputUI.Input
                name="shippingPackageSize"
                disabled={!shippingConfig?.shippingPackageSizeEditable}
                $inputSize="large"
                id="shippingPackageSize__input"
                type="text"
                value={getInputValue()}
                $hasLabel={Boolean(getInputValue())}
                aria-labelledby="shippingPackageSize__label"
                $readOnly
              />
              <InputUI.Label
                htmlFor="shippingPackageSize__input"
                $size="large"
                id="shippingPackageSize__label"
                $hasValue={Boolean(getInputValue())}
              >
                Product size
              </InputUI.Label>
            </InputUI.Container>
          </InputUI.Box>
        </InputUI.Root>
        {error ? <InputUI.Error>{error}</InputUI.Error> : null}
      </S.PackageSizeInput>
    )
  },
)

export const PackageSizeInputFormik = withFormikSupport(PackageSizeInput)
